<template>
  <CashierLayout>
    <template #content>
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 class="h4 mb-1">Inventaires à compter ({{ totalRows }})</h1>
          <small class="text-muted">Sélectionnez un brouillon ouvert pour démarrer ou continuer le comptage.</small>
        </div>

        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="isLoading"
          @click="fetchDraftInventories"
        >
          <i class="fa fa-rotate-right me-1"></i>
          Actualiser
        </button>
      </div>

      <div v-if="isLoading" class="alert alert-info py-2">
        Chargement des inventaires brouillon...
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>

      <div v-if="!isLoading && !draftInventories.length" class="empty-inventory-state">
        <i class="fa fa-clipboard-check"></i>
        <div>
          <strong>Aucun inventaire brouillon</strong>
          <span>Un gérant doit créer un brouillon depuis le backoffice avant le comptage.</span>
        </div>
      </div>

      <div v-else class="row g-3">
        <div v-for="inventory in draftInventories" :key="inventory.id" class="col-xl-4 col-md-6">
          <button type="button" class="inventory-draft-card" @click="openInventory(inventory)">
            <div class="inventory-draft-header">
              <div>
                <span class="inventory-reference">{{ inventory.reference }}</span>
                <small>{{ $format.date(inventory.created_at) }}</small>
              </div>
              <span class="badge" :class="inventory.type === 'global' ? 'bg-primary' : 'bg-info text-dark'">
                {{ inventory.type === 'global' ? 'Global' : 'Partiel' }}
              </span>
            </div>

            <div class="inventory-draft-body">
              <div class="inventory-rayons">
                <i class="fa fa-layer-group"></i>
                <span v-if="inventory.type === 'global'">Tous les rayons</span>
                <span v-else-if="scopeRayons(inventory).length" class="inventory-rayon-chips">
                  <span
                    v-for="rayon in scopeRayons(inventory)"
                    :key="rayon.id || rayon.name"
                    class="inventory-rayon-chip"
                    :style="{ borderColor: rayonColor(rayon.color) }"
                  >
                    <span class="rayon-color-dot" :style="{ backgroundColor: rayonColor(rayon.color) }"></span>
                    <span>{{ rayon.name }}</span>
                  </span>
                </span>
                <span v-else>Rayons sélectionnés</span>
              </div>

              <div class="inventory-progress-row">
                <span>{{ countedCount(inventory) }} / {{ totalCount(inventory) }} produits comptés</span>
                <strong>{{ progressPercent(inventory) }}%</strong>
              </div>
              <div class="progress inventory-progress">
                <div
                  class="progress-bar bg-success"
                  role="progressbar"
                  :style="{ width: `${progressPercent(inventory)}%` }"
                ></div>
              </div>
            </div>

            <div class="inventory-draft-footer">
              <span>{{ remainingCount(inventory) }} restant(s)</span>
              <span class="btn btn-success btn-sm">
                <i class="fa fa-arrow-right me-1"></i>
                {{ countedCount(inventory) > 0 ? 'Continuer' : 'Commencer' }}
              </span>
            </div>
          </button>
        </div>
      </div>
    </template>
  </CashierLayout>
</template>

<script>
import { normalizeRayonColor } from '@/utils/rayonColor';

export default {
  name: 'CashierInventoriesView',
  data() {
    return {
      draftInventories: [],
      isLoading: false,
      errorMessage: '',
    };
  },
  mounted() {
    this.fetchDraftInventories();
  },
  computed: {
    totalRows() {
      return this.draftInventories.length;
    },
  },
  methods: {
    async fetchDraftInventories() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.get('/inventories', {
          params: {
            status: 'draft',
            paginate: 1,
            per_page: 100,
            sort_by: 'created_at',
            sort_dir: 'desc',
          },
        });
        this.draftInventories = response.data?.data || [];
      } catch (error) {
        console.error('Erreur chargement inventaires brouillon', error);
        this.errorMessage = 'Impossible de charger les inventaires brouillon.';
      } finally {
        this.isLoading = false;
      }
    },

    openInventory(inventory) {
      this.$router.push({
        name: 'CashierInventoryCount',
        params: { id: inventory.id },
      });
    },

    totalCount(inventory) {
      return Number(inventory.items_count || inventory.total_items || 0);
    },

    countedCount(inventory) {
      return Number(inventory.counted_items_count || 0);
    },

    remainingCount(inventory) {
      return Math.max(0, this.totalCount(inventory) - this.countedCount(inventory));
    },

    progressPercent(inventory) {
      const total = this.totalCount(inventory);
      if (!total) return 0;

      return Math.round((this.countedCount(inventory) / total) * 100);
    },

    scopeLabel(inventory) {
      if (inventory.type === 'global') {
        return 'Tous les rayons';
      }

      const rayons = inventory.scope?.rayons || [];
      if (!rayons.length) {
        return 'Rayons sélectionnés';
      }

      return rayons.map((rayon) => rayon.name).join(', ');
    },

    scopeRayons(inventory) {
      return inventory.scope?.rayons || [];
    },

    rayonColor(color) {
      return normalizeRayonColor(color);
    },
  },
};
</script>

<style scoped>
.inventory-draft-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #172033;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 214px;
  padding: 1rem;
  text-align: left;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
  width: 100%;
}

.inventory-draft-card:hover,
.inventory-draft-card:focus {
  border-color: rgba(25, 135, 84, 0.5);
  box-shadow: 0 0.45rem 1.2rem rgba(15, 23, 42, 0.08);
  outline: none;
  transform: translateY(-1px);
}

.inventory-draft-header,
.inventory-draft-footer,
.inventory-progress-row,
.inventory-rayons {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.inventory-draft-header > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.inventory-reference {
  font-size: 0.92rem;
  font-weight: 900;
}

.inventory-draft-header small,
.inventory-draft-footer,
.inventory-rayons {
  color: #64748b;
  font-size: 0.82rem;
}

.inventory-rayons {
  justify-content: flex-start;
}

.inventory-rayons span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.inventory-rayon-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  white-space: normal !important;
}

.inventory-rayon-chip {
  align-items: center;
  background: #ffffff;
  border: 1px solid;
  border-radius: 999px;
  display: inline-flex;
  gap: 0.35rem;
  padding: 0.1rem 0.5rem;
}

.rayon-color-dot {
  border: 1px solid rgba(15, 23, 42, 0.16);
  border-radius: 999px;
  display: inline-block;
  height: 0.72rem;
  width: 0.72rem;
}

.inventory-draft-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0.85rem;
}

.inventory-progress {
  height: 7px;
}

.empty-inventory-state {
  align-items: center;
  background: #ffffff;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  color: #64748b;
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
}

.empty-inventory-state i {
  color: #198754;
  font-size: 1.6rem;
}

.empty-inventory-state div {
  display: flex;
  flex-direction: column;
}

.empty-inventory-state strong {
  color: #172033;
}

@media (max-width: 575.98px) {
  .inventory-draft-header,
  .inventory-draft-footer {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
